import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { Button, Form, Pagination, Spinner, Table } from 'react-bootstrap';
import queries from '../utils/queries';

type CurationChartReference = {
  id: string;
  date: string;
  source: string;
  glplayLink?: string | null;
};

type CurationChartReferencesQuery = {
  curation: {
    curationChartReferences: {
      totalCount: number;
      list: CurationChartReference[];
    };
  };
};

const PAGE_SIZE = 50;

function getCurrentMonthStart() {
  return `${new Date().toISOString().slice(0, 7)}-01`;
}

function getCurrentDate() {
  return new Date().toISOString().slice(0, 10);
}

export default function CurationChartReferences() {
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState(getCurrentMonthStart());
  const [dateTo, setDateTo] = useState(getCurrentDate());
  const offset = (page - 1) * PAGE_SIZE;
  const { data, loading } = useQuery<CurationChartReferencesQuery>(queries.CURATION_CHART_REFERENCES, {
    variables: { limit: PAGE_SIZE, offset, dateFrom, dateTo },
    fetchPolicy: 'cache-and-network',
  });

  const references = data?.curation.curationChartReferences.list ?? [];
  const totalCount = data?.curation.curationChartReferences.totalCount ?? 0;
  const maxPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="p-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">Chart References</h2>
        {loading && <Spinner animation="border" size="sm" />}
      </div>
      <div className="d-flex mb-3">
        <Form.Group controlId="chart-reference-date-from" className="mr-3">
          <Form.Label>From</Form.Label>
          <Form.Control
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.currentTarget.value);
              setPage(1);
            }}
          />
        </Form.Group>
        <Form.Group controlId="chart-reference-date-to">
          <Form.Label>To</Form.Label>
          <Form.Control
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.currentTarget.value);
              setPage(1);
            }}
          />
        </Form.Group>
      </div>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th>Date</th>
            <th>Source</th>
            <th>Links</th>
          </tr>
        </thead>
        <tbody>
          {references.length === 0 && (
            <tr>
              <td colSpan={3}>No chart references found.</td>
            </tr>
          )}
          {references.map((reference) => (
            <tr key={reference.id}>
              <td>{reference.date}</td>
              <td>{reference.source}</td>
              <td>
                {reference.glplayLink ? (
                  <Button
                    as="a"
                    href={reference.glplayLink}
                    target="_blank"
                    rel="noreferrer"
                    size="sm"
                    variant="primary"
                  >
                    Open GLPlay
                  </Button>
                ) : (
                  '-'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {maxPage > 1 && (
        <Pagination>
          <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
          <Pagination.Item active>{page}</Pagination.Item>
          <Pagination.Next disabled={page === maxPage} onClick={() => setPage(page + 1)} />
        </Pagination>
      )}
    </div>
  );
}
